import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import {
  FormControlLabel,
  IconButton,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { EntitlementType } from "@models";
import { useEffect, useState } from "react";
import GenericModal from "src/components/GenericModal/GenericModal";
import { FunctionalGroup } from "src/types";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    name: string;
    acronym: string;
    administerAllTrainings: boolean;
  }) => void;
  existingGroup?: FunctionalGroup | null;
  isLoading?: boolean;
};

const generateAcronym = (name: string): string => {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0].toUpperCase())
    .join("")
    .slice(0, 10);
};

export const CreateFunctionalGroupModal = ({
  isOpen,
  onClose,
  onSave,
  existingGroup,
  isLoading,
}: Props) => {
  const [name, setName] = useState("");
  const [acronym, setAcronym] = useState("");
  const [acronymTouched, setAcronymTouched] = useState(false);
  const [administerAllTrainings, setAdministerAllTrainings] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (existingGroup) {
        setName(existingGroup.name);
        setAcronym(existingGroup.acronym);
        setAcronymTouched(true);
        setAdministerAllTrainings(
          existingGroup.entitlements?.some(
            (entitlement) =>
              entitlement.type === EntitlementType.ADMINISTER_ALL_TRAININGS &&
              entitlement.value_bool === true
          ) ?? false
        );
      } else {
        setName("");
        setAcronym("");
        setAcronymTouched(false);
        setAdministerAllTrainings(false);
      }
    }
  }, [isOpen, existingGroup]);

  const handleNameChange = (value: string) => {
    setName(value);
    if (!acronymTouched) {
      setAcronym(generateAcronym(value));
    }
  };

  const isValid = name.trim().length > 0 && acronym.trim().length > 0;

  return (
    <GenericModal
      isOpen={isOpen}
      handleCancel={onClose}
      handleSave={() => {
        onSave({
          name: name.trim(),
          acronym: acronym.trim(),
          administerAllTrainings,
        });
      }}
      isDisabled={!isValid}
      isLoading={isLoading}
      title={existingGroup ? "Edit Functional Group" : "Create Functional Group"}
      saveText={existingGroup ? "Update" : "Create"}
    >
      <div className="flex flex-col gap-3">
        <TextField
          label="Name"
          size="small"
          fullWidth
          value={name}
          onChange={(e) => handleNameChange(e.target.value)}
          autoFocus
        />
        <TextField
          label="Acronym"
          size="small"
          fullWidth
          value={acronym}
          onChange={(e) => {
            setAcronym(e.target.value);
            setAcronymTouched(true);
          }}
          helperText="Auto-suggested from name initials (max 10 characters)"
          inputProps={{ maxLength: 10 }}
        />
        <div className="flex flex-col gap-1">
          <Typography variant="subtitle2">Permissions</Typography>
          <div className="flex items-center gap-1">
            <FormControlLabel
              control={
                <Switch
                  checked={administerAllTrainings}
                  onChange={(e) => setAdministerAllTrainings(e.target.checked)}
                />
              }
              label="Can Manage Trainings"
            />
            <Tooltip
              arrow
              title="Let anyone in this functional group be able to assign trainings. Usually only needed for a Quality Manager functional group."
            >
              <IconButton
                aria-label="Training management permission details"
                size="small"
              >
                <InfoOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </div>
        </div>
      </div>
    </GenericModal>
  );
};
